// src/components/referral/ReferralManagement.jsx
// Advanced Referral Management UI Component

import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { 
  Users, Calendar, AlertTriangle, CheckCircle, Clock, 
  Trash2, RotateCcw, Plus, DollarSign, Info, X, 
  CalendarDays, UserX, UserCheck, Award
} from 'lucide-react';

const ReferralManagement = () => {
  const [managementData, setManagementData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [removeReason, setRemoveReason] = useState('');
  const [messageToReferrer, setMessageToReferrer] = useState('');

  const functions = getFunctions();

  useEffect(() => {
    loadManagementData();
  }, []);

  const loadManagementData = async () => {
    try {
      setLoading(true);
      const getReferralManagementData = httpsCallable(functions, 'getReferralManagementData');
      const result = await getReferralManagementData();
      
      if (result.data.success) {
        setManagementData(result.data);
      }
    } catch (error) {
      console.error('Error loading referral management data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveReferrer = async () => {
    try {
      setActionLoading(true);
      const removeReferrer = httpsCallable(functions, 'removeReferrer');
      const result = await removeReferrer({
        reason: removeReason,
        messageToReferrer: messageToReferrer
      });
      
      if (result.data.success) {
        setShowRemoveModal(false);
        setRemoveReason('');
        setMessageToReferrer('');
        await loadManagementData();
        alert('Referrer removed successfully. You can reinstate them anytime if needed.');
      } else {
        alert(result.data.error || 'Failed to remove referrer');
      }
    } catch (error) {
      console.error('Error removing referrer:', error);
      alert('Failed to remove referrer. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReinstateReferrer = async () => {
    if (!window.confirm('Reinstate your referrer? They will resume earning 2% of your future earnings.')) {
      return;
    }

    try {
      setActionLoading(true);
      const reinstateReferrer = httpsCallable(functions, 'reinstateReferrer');
      const result = await reinstateReferrer();
      
      if (result.data.success) {
        await loadManagementData();
        alert(result.data.message);
      } else {
        alert(result.data.error || 'Failed to reinstate referrer');
      }
    } catch (error) {
      console.error('Error reinstating referrer:', error);
      alert('Failed to reinstate referrer. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleExtendReferral = async () => {
    try {
      setActionLoading(true);
      const extendReferralPeriod = httpsCallable(functions, 'extendReferralPeriod');
      const result = await extendReferralPeriod();
      
      if (result.data.success) {
        setShowExtendModal(false);
        await loadManagementData();
        alert('Referral period extended by 6 months!');
      } else {
        alert(result.data.error || 'Failed to extend referral period');
      }
    } catch (error) {
      console.error('Error extending referral:', error);
      alert('Failed to extend referral period. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <Users className="animate-pulse" size={48} style={{ margin: '0 auto', color: '#7C3AED' }} />
        <p style={{ marginTop: '20px', color: '#6B7280' }}>Loading referral information...</p>
      </div>
    );
  }

  if (!managementData?.hasReferrer) {
    return (
      <div style={{
        backgroundColor: '#F3F4F6',
        borderRadius: '12px',
        padding: '40px',
        textAlign: 'center',
        border: '2px dashed #D1D5DB'
      }}>
        <Users size={64} style={{ color: '#9CA3AF', margin: '0 auto 20px' }} />
        <h3 style={{ color: '#374151', fontSize: '20px', fontWeight: 'bold', margin: '0 0 12px 0' }}>
          No Referrer Found
        </h3>
        <p style={{ color: '#6B7280', margin: '0 0 24px 0', maxWidth: '400px', margin: '0 auto' }}>
          You don't currently have anyone referring you to RequestASong. 
          If someone referred you, make sure you used their referral code when signing up.
        </p>
      </div>
    );
  }

  const { referrerData, referralStatus, expirationDate, daysUntilExpiration, 
          hasBeenExtended, canExtend, canRemoveReferrer, daysUntilCanRemove } = managementData;

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        backgroundColor: '#1F2937',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        color: 'white'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
          <Users size={32} />
          <div>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: '0 0 4px 0' }}>
              Referral Management
            </h2>
            <p style={{ color: '#9CA3AF', margin: 0 }}>
              Manage your referral arrangement
            </p>
          </div>
        </div>
      </div>

      {/* Current Referrer Info */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        border: '1px solid #E5E7EB'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '20px' }}>
          <div>
            <h3 style={{ fontSize: '20px', fontWeight: 'bold', margin: '0 0 8px 0', color: '#1F2937' }}>
              Current Referrer
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <UserCheck size={20} style={{ color: '#10B981' }} />
              <span style={{ fontSize: '18px', fontWeight: '600', color: '#1F2937' }}>
                {referrerData.name}
              </span>
              {referralStatus === 'active' && (
                <span style={{
                  backgroundColor: '#D1FAE5',
                  color: '#065F46',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  Active
                </span>
              )}
              {referralStatus === 'removed' && (
                <span style={{
                  backgroundColor: '#FEE2E2',
                  color: '#991B1B',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  Removed
                </span>
              )}
            </div>
          </div>
          
          <div style={{ textAlign: 'right' }}>
            <p style={{ color: '#6B7280', fontSize: '14px', margin: '0 0 4px 0' }}>
              Total Paid
            </p>
            <p style={{ fontSize: '20px', fontWeight: 'bold', color: '#059669', margin: 0 }}>
              {formatCurrency(referrerData.totalEarningsPaid)}
            </p>
          </div>
        </div>

        {/* Status Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px' }}>
          <div style={{ backgroundColor: '#F9FAFB', padding: '16px', borderRadius: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <Calendar size={16} style={{ color: '#6B7280' }} />
              <span style={{ fontSize: '14px', color: '#6B7280' }}>Referral Started</span>
            </div>
            <p style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937', margin: 0 }}>
              {formatDate(referrerData.referralAcceptedAt?.toDate())}
            </p>
          </div>

          {expirationDate && (
            <div style={{ backgroundColor: '#F9FAFB', padding: '16px', borderRadius: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <CalendarDays size={16} style={{ color: '#6B7280' }} />
                <span style={{ fontSize: '14px', color: '#6B7280' }}>
                  {daysUntilExpiration > 0 ? 'Expires On' : 'Expired On'}
                </span>
              </div>
              <p style={{ 
                fontSize: '16px', 
                fontWeight: '600', 
                color: daysUntilExpiration <= 7 && daysUntilExpiration > 0 ? '#DC2626' : '#1F2937', 
                margin: 0 
              }}>
                {formatDate(expirationDate)}
                {daysUntilExpiration > 0 && (
                  <span style={{ fontSize: '12px', display: 'block', marginTop: '2px' }}>
                    ({daysUntilExpiration} day{daysUntilExpiration === 1 ? '' : 's'} remaining)
                  </span>
                )}
              </p>
            </div>
          )}

          {hasBeenExtended && (
            <div style={{ backgroundColor: '#EEF2FF', padding: '16px', borderRadius: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <Award size={16} style={{ color: '#7C3AED' }} />
                <span style={{ fontSize: '14px', color: '#7C3AED' }}>Extension Used</span>
              </div>
              <p style={{ fontSize: '16px', fontWeight: '600', color: '#7C3AED', margin: 0 }}>
                6 Months Added
              </p>
            </div>
          )}
        </div>

        {/* Expiration Warning */}
        {daysUntilExpiration > 0 && daysUntilExpiration <= 7 && (
          <div style={{
            backgroundColor: '#FEF3C7',
            border: '1px solid #F59E0B',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px',
            display: 'flex',
            gap: '12px'
          }}>
            <AlertTriangle size={20} style={{ color: '#D97706', flexShrink: 0 }} />
            <div>
              <p style={{ color: '#92400E', margin: '0 0 8px 0', fontWeight: '600' }}>
                Referral Arrangement Expiring Soon
              </p>
              <p style={{ color: '#78350F', margin: '0 0 8px 0', fontSize: '14px' }}>
                Your referral arrangement expires in {daysUntilExpiration} day{daysUntilExpiration === 1 ? '' : 's'}. 
                {canExtend ? ' You can extend it for 6 more months if you wish.' : ' This arrangement will end automatically.'}
              </p>
              {canExtend && (
                <button
                  onClick={() => setShowExtendModal(true)}
                  disabled={actionLoading}
                  style={{
                    backgroundColor: '#F59E0B',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: actionLoading ? 'not-allowed' : 'pointer',
                    opacity: actionLoading ? 0.7 : 1
                  }}
                >
                  Extend for 6 Months
                </button>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          {referralStatus === 'removed' && (
            <button
              onClick={handleReinstateReferrer}
              disabled={actionLoading}
              style={{
                backgroundColor: '#10B981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: actionLoading ? 'not-allowed' : 'pointer',
                opacity: actionLoading ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <RotateCcw size={16} />
              Reinstate Referrer
            </button>
          )}

          {referralStatus === 'active' && (
            <button
              onClick={() => setShowRemoveModal(true)}
              disabled={!canRemoveReferrer || actionLoading}
              style={{
                backgroundColor: canRemoveReferrer ? '#DC2626' : '#9CA3AF',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: canRemoveReferrer && !actionLoading ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <UserX size={16} />
              Remove Referrer
            </button>
          )}
        </div>

        {/* Cannot Remove Warning */}
        {!canRemoveReferrer && referralStatus === 'active' && daysUntilCanRemove > 0 && (
          <div style={{
            backgroundColor: '#F3F4F6',
            border: '1px solid #D1D5DB',
            borderRadius: '8px',
            padding: '12px',
            marginTop: '12px',
            display: 'flex',
            gap: '8px'
          }}>
            <Info size={16} style={{ color: '#6B7280', flexShrink: 0 }} />
            <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
              You can remove your referrer {daysUntilCanRemove} day{daysUntilCanRemove === 1 ? '' : 's'} after account creation 
              (30-day minimum period for anti-fraud protection).
            </p>
          </div>
        )}
      </div>

      {/* Remove Referrer Modal */}
      {showRemoveModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0, color: '#1F2937' }}>
                Remove Referrer
              </h3>
              <button
                onClick={() => setShowRemoveModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <X size={20} style={{ color: '#6B7280' }} />
              </button>
            </div>

            <div style={{
              backgroundColor: '#FEF2F2',
              border: '1px solid #FCA5A5',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px'
            }}>
              <p style={{ color: '#991B1B', margin: '0 0 8px 0', fontWeight: '600' }}>
                ⚠️ Are you sure you want to remove {referrerData.name}?
              </p>
              <ul style={{ color: '#7F1D1D', margin: '0', paddingLeft: '20px', fontSize: '14px' }}>
                <li>They will stop earning 2% of your future earnings</li>
                <li>You can reinstate them later if needed</li>
                <li>Past payments cannot be reversed</li>
              </ul>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>
                Reason for removal (optional):
              </label>
              <select
                value={removeReason}
                onChange={(e) => setRemoveReason(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #D1D5DB',
                  backgroundColor: 'white'
                }}
              >
                <option value="">Select a reason</option>
                <option value="personal_dispute">Personal dispute</option>
                <option value="testing_system">Testing the system</option>
                <option value="no_longer_needed">No longer needed</option>
                <option value="prefer_different_person">Prefer different person</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>
                Message to {referrerData.name} (optional):
              </label>
              <textarea
                value={messageToReferrer}
                onChange={(e) => setMessageToReferrer(e.target.value)}
                placeholder="Let them know why you're removing them..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #D1D5DB',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowRemoveModal(false)}
                disabled={actionLoading}
                style={{
                  backgroundColor: '#F3F4F6',
                  color: '#374151',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: actionLoading ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleRemoveReferrer}
                disabled={actionLoading}
                style={{
                  backgroundColor: '#DC2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: actionLoading ? 'not-allowed' : 'pointer',
                  opacity: actionLoading ? 0.7 : 1
                }}
              >
                {actionLoading ? 'Removing...' : 'Remove Referrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Extend Referral Modal */}
      {showExtendModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '450px',
            width: '90%'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0, color: '#1F2937' }}>
                Extend Referral Period
              </h3>
              <button
                onClick={() => setShowExtendModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <X size={20} style={{ color: '#6B7280' }} />
              </button>
            </div>

            <div style={{
              backgroundColor: '#EEF2FF',
              border: '1px solid #7C3AED',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px'
            }}>
              <p style={{ color: '#5B21B6', margin: '0 0 8px 0', fontWeight: '600' }}>
                ✨ Extend your arrangement with {referrerData.name}
              </p>
              <ul style={{ color: '#6D28D9', margin: '0', paddingLeft: '20px', fontSize: '14px' }}>
                <li>Add 6 more months to your referral period</li>
                <li>This is a one-time extension only</li>
                <li>They will continue earning 2% of your earnings</li>
              </ul>
            </div>

            <p style={{ color: '#6B7280', fontSize: '14px', marginBottom: '20px' }}>
              Current expiration: <strong>{formatDate(expirationDate)}</strong><br/>
              New expiration: <strong>{formatDate(new Date(expirationDate.getTime() + (6 * 30 * 24 * 60 * 60 * 1000)))}</strong>
            </p>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowExtendModal(false)}
                disabled={actionLoading}
                style={{
                  backgroundColor: '#F3F4F6',
                  color: '#374151',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: actionLoading ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleExtendReferral}
                disabled={actionLoading}
                style={{
                  backgroundColor: '#7C3AED',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: actionLoading ? 'not-allowed' : 'pointer',
                  opacity: actionLoading ? 0.7 : 1
                }}
              >
                {actionLoading ? 'Extending...' : 'Extend for 6 Months'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralManagement;